import type { VenueReservation } from "@/graphql/operations";
import icons from "@/tools/icons";
import { Box, ButtonBase, Stack, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { format } from "date-fns";
import ReservationCalendarReservationTable from "./ReservationCalendarReservationTable";

type ReservationCalendarDayCellProps = {
  day: Date;
  dateKey: string;
  isCurrentMonth: boolean;
  isDayToday: boolean;
  dayReservations: VenueReservation[];
  onDateClick?: (date: string) => void;
  maxVisibleReservations: number;
  canSelectReservation: boolean;
  selectReservation?: (reservationId: string) => void;
};

export default function ReservationCalendarDayCell({
  day,
  dateKey,
  isCurrentMonth,
  isDayToday,
  dayReservations,
  onDateClick,
  maxVisibleReservations,
  canSelectReservation,
  selectReservation,
}: ReservationCalendarDayCellProps) {
  const handleSelectReservation = canSelectReservation
    ? (reservationId: string) => {
        onDateClick?.(dateKey);
        selectReservation?.(reservationId);
      }
    : undefined;

  return (
    <ButtonBase
      onClick={() => onDateClick?.(dateKey)}
      sx={(theme) => ({
        borderRadius: 1,
        textAlign: "left",
        width: "100%",
        alignItems: "stretch",
        "&:hover .calendar-day-box": {
          borderColor: "primary.main",
          backgroundImage: `linear-gradient(${theme.palette.action.hover}, ${theme.palette.action.hover})`,
        },
      })}
    >
      <Box
        className="calendar-day-box"
        sx={(theme) => ({
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          padding: 1,
          width: "100%",
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
          containerType: "inline-size",
          backgroundColor: isDayToday
            ? alpha(theme.palette.primary.main, 0.2)
            : theme.palette.background.paper,
          opacity: isCurrentMonth ? 1 : 0.4,
          "& .calendar-cell-count": {
            display: "inline-flex",
          },
          "@container (max-width: 92px)": {
            "& .calendar-cell-count": {
              display: "none",
            },
          },
        })}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="subtitle2">{format(day, "d")}</Typography>
          {isDayToday && (
            <icons.Calendar fontSize="inherit" sx={{ fontSize: "0.95rem" }} />
          )}
        </Stack>
        <Box sx={{ flexGrow: 1 }}>
          <ReservationCalendarReservationTable
            dayReservations={dayReservations}
            maxVisibleReservations={maxVisibleReservations}
            canSelectReservation={canSelectReservation}
            selectReservation={handleSelectReservation}
          />
        </Box>
      </Box>
    </ButtonBase>
  );
}
